import * as signalR from '@microsoft/signalr'
import type { AskQuestionRequest } from '@/types'

// ─────────────────────────────────────────────
// signalr.ts
// Manages the SignalR connection to ChatHub.
// Provides token-by-token streaming via
// ReceiveToken events — each event carries one
// LLM output token as it is generated.
//
// Hub events (must match ChatHub.cs exactly):
//   Client → Server : SendMessage(AskQuestionRequest)
//   Server → Client : ReceiveToken(string token)
//   Server → Client : ReceiveComplete(string sessionId)
//   Server → Client : ReceiveError(string error)
// ─────────────────────────────────────────────

let _connection: signalR.HubConnection | null = null

// Build hub URL — empty baseUrl = Vite proxy in dev
function buildHubUrl(apiUrl: string): string {
  const base = apiUrl || import.meta.env.VITE_API_URL || ''
  return `${base}/hubs/chat`
}

export function createConnection(apiUrl: string, token: string): signalR.HubConnection {
  _connection = new signalR.HubConnectionBuilder()
    .withUrl(buildHubUrl(apiUrl), {
      accessTokenFactory: () => token,
    })
    .withAutomaticReconnect([0, 2000, 5000, 10000])
    .configureLogging(signalR.LogLevel.Warning)
    .build()

  return _connection
}

export function getConnection(): signalR.HubConnection | null {
  return _connection
}

export async function startConnection(apiUrl: string, token: string): Promise<signalR.HubConnection> {
  const connection = createConnection(apiUrl, token)

  try {
    await connection.start()
    console.info('[SignalR] Connected to ChatHub')
  } catch (err) {
    console.warn('[SignalR] Connection failed — widget will use REST fallback', err)
    throw err
  }

  return connection
}

export async function stopConnection(): Promise<void> {
  if (_connection) {
    await _connection.stop()
    _connection = null
    console.info('[SignalR] Disconnected from ChatHub')
  }
}

// ── Send a message and wire streaming callbacks ──
export function sendMessage(
  connection: signalR.HubConnection,
  request: AskQuestionRequest,
  onToken: (token: string) => void,         // Called for each streamed token
  onComplete: (sessionId: string) => void,  // Called when stream finishes — receives sessionId
  onError: (error: string) => void          // Called on hub error
): void {
  // Wire event handlers before sending — avoids race condition
  // where first token arrives before handlers are registered
  connection.on('ReceiveToken', onToken)

  connection.on('ReceiveComplete', (sessionId: string) => {
    // Clean up handlers after stream ends — prevents duplicate handlers
    // if sendMessage is called again on the same connection
    connection.off('ReceiveToken', onToken)
    connection.off('ReceiveComplete')
    connection.off('ReceiveError')
    onComplete(sessionId ?? '')
  })

  connection.on('ReceiveError', (error: string) => {
    connection.off('ReceiveToken', onToken)
    connection.off('ReceiveComplete')
    connection.off('ReceiveError')
    onError(error)
  })

  // Fire the hub method — ChatHub.SendMessage(AskQuestionRequest)
  connection.invoke('SendMessage', request).catch((err) => {
    onError(err?.message || 'Failed to send message to hub')
  })
}